import { api } from "./api.js";

export type RealtimeStatus = "idle" | "connecting" | "live" | "error";

export interface RealtimeCallbacks {
  onStatus?: (s: RealtimeStatus) => void;
  onUserTranscript?: (text: string) => void;
  onAssistantDelta?: (delta: string) => void;
  onAssistantTranscript?: (text: string) => void;
  onToolCall?: (tool: string, args: Record<string, unknown>) => void;
  onError?: (message: string) => void;
  /** Returns the active conversation id so tool calls hit the right worker. */
  getConversationId?: () => string | null | undefined;
}

/**
 * WebRTC client for gpt-realtime-2. Audio streams browser <-> OpenAI directly via
 * an ephemeral token; tool calls are forwarded to the Atlas gateway and their
 * results fed back into the realtime session so the voice brain narrates them.
 */
export class RealtimeClient {
  private pc?: RTCPeerConnection;
  private dc?: RTCDataChannel;
  private audioEl?: HTMLAudioElement;
  private micStream?: MediaStream;
  private micTrack?: MediaStreamTrack;
  private audioCtx?: AudioContext;
  private analyser?: AnalyserNode;
  // Track tool executions for the current model response so we trigger exactly
  // ONE follow-up response after all (possibly parallel) tool calls finish.
  private pendingTools: Promise<void>[] = [];
  private toolsThisResponse = 0;
  private lastTranscript = "";

  constructor(private cb: RealtimeCallbacks = {}) {}

  /**
   * Open the single realtime session (the conversation owner). `listening`
   * controls whether the mic is enabled; text works either way over the data
   * channel, so the channel can be "open but not listening" (text mode).
   */
  async connect(opts: { listening?: boolean; conversationId?: string | null } = {}): Promise<void> {
    this.cb.onStatus?.("connecting");
    try {
      const { token, iceServers, model } = await api.realtimeToken(opts.conversationId ?? undefined);
      const ephemeral = extractToken(token);
      if (!ephemeral) throw new Error("No ephemeral token returned");

      const pc = new RTCPeerConnection({ iceServers });
      this.pc = pc;

      // Remote audio playback + feed into the analyser for the waveform.
      this.audioEl = new Audio();
      this.audioEl.autoplay = true;
      pc.ontrack = (e) => {
        if (this.audioEl) this.audioEl.srcObject = e.streams[0];
        try {
          const a = this.ensureAnalyser();
          this.audioCtx!.createMediaStreamSource(e.streams[0]).connect(a);
        } catch {
          /* analyser is best-effort */
        }
      };

      // Cost-safe: only capture/stream the mic when actually listening. In text
      // mode we use a receive-only audio channel, so NO audio-input tokens are
      // billed while you type. Switching to voice reconnects with the mic.
      if (opts.listening) {
        try {
          // Explicit AEC/NS/AGC: browser acoustic echo cancellation needs these
          // on, and the remote audio plays via an <audio> element (above) so the
          // browser's AEC can use it as the echo reference.
          this.micStream = await navigator.mediaDevices.getUserMedia({
            audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
          });
          this.micTrack = this.micStream.getAudioTracks()[0];
          pc.addTrack(this.micTrack, this.micStream);
          try {
            const a = this.ensureAnalyser();
            this.audioCtx!.createMediaStreamSource(this.micStream).connect(a);
          } catch {
            /* analyser is best-effort */
          }
        } catch {
          pc.addTransceiver("audio", { direction: "recvonly" });
          this.micTrack = undefined;
        }
      } else {
        pc.addTransceiver("audio", { direction: "recvonly" });
        this.micTrack = undefined;
      }

      // Data channel for events + tool calls.
      const dc = pc.createDataChannel("oai-events");
      this.dc = dc;
      const dcOpen = new Promise<void>((resolve) => {
        dc.onopen = () => {
          this.cb.onStatus?.("live");
          resolve();
        };
      });
      dc.onmessage = (e) => this.handleEvent(JSON.parse(e.data));

      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);

      const sdpRes = await fetch(`https://api.openai.com/v1/realtime/calls?model=${encodeURIComponent(model)}`, {
        method: "POST",
        body: offer.sdp,
        headers: { Authorization: `Bearer ${ephemeral}`, "Content-Type": "application/sdp" },
      });
      if (!sdpRes.ok) throw new Error(`Realtime SDP exchange failed: ${sdpRes.status}`);
      const answer = { type: "answer" as const, sdp: await sdpRes.text() };
      await pc.setRemoteDescription(answer);

      // Don't return until the data channel is actually open, so the first
      // typed/spoken message isn't dropped on a not-yet-ready channel.
      await Promise.race([dcOpen, new Promise<void>((r) => setTimeout(r, 8000))]);
    } catch (err) {
      this.cb.onStatus?.("error");
      this.cb.onError?.((err as Error).message);
      throw err;
    }
  }

  private handleEvent(ev: { type: string; [k: string]: unknown }): void {
    switch (ev.type) {
      case "conversation.item.input_audio_transcription.completed":
        this.cb.onUserTranscript?.(String(ev.transcript ?? ""));
        break;
      case "response.output_audio_transcript.delta":
      case "response.audio_transcript.delta":
        if (ev.delta) this.cb.onAssistantDelta?.(String(ev.delta));
        break;
      case "response.output_audio_transcript.done":
      case "response.audio_transcript.done": {
        // Some API versions emit both event names for one response; de-dupe.
        const transcript = String(ev.transcript ?? "");
        if (transcript && transcript !== this.lastTranscript) {
          this.lastTranscript = transcript;
          this.cb.onAssistantTranscript?.(transcript);
        }
        break;
      }
      case "response.function_call_arguments.done":
        // Execute the tool, but DON'T request a new response yet — wait until the
        // whole response is done so parallel tool calls produce a single follow-up.
        this.toolsThisResponse++;
        this.pendingTools.push(this.dispatchToolCall(ev));
        break;
      case "response.done": {
        if (this.toolsThisResponse > 0) {
          const pend = this.pendingTools;
          this.pendingTools = [];
          this.toolsThisResponse = 0;
          // After all tool outputs are submitted, ask for ONE narration response.
          void Promise.allSettled(pend).then(() => {
            if (this.dc && this.dc.readyState === "open") {
              this.dc.send(JSON.stringify({ type: "response.create" }));
            }
          });
        }
        break;
      }
      case "error":
        this.cb.onError?.(JSON.stringify(ev.error ?? ev));
        break;
      default:
        break;
    }
  }

  private async dispatchToolCall(ev: { [k: string]: unknown }): Promise<void> {
    const name = String(ev.name ?? "");
    const callId = String(ev.call_id ?? ev.callId ?? "");
    let args: Record<string, unknown> = {};
    try {
      args = JSON.parse(String(ev.arguments ?? "{}"));
    } catch {
      args = {};
    }
    this.cb.onToolCall?.(name, args);
    try {
      const conversationId = this.cb.getConversationId?.();
      const result = await api.callTool(name, args, callId, conversationId);
      this.sendToolOutput(callId, result.summary);
    } catch (err) {
      this.sendToolOutput(callId, `Tool failed: ${(err as Error).message}`);
    }
  }

  /** Submit a function_call_output (truncated so the voice model stays sane). */
  private sendToolOutput(callId: string, output: string): void {
    if (!this.dc || this.dc.readyState !== "open") return;
    const trimmed = output.length > 1500 ? output.slice(0, 1500) + "… (truncated)" : output;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: { type: "function_call_output", call_id: callId, output: trimmed },
      }),
    );
  }

  /** Hard-cancel: stop the current model response and drop pending tool calls. */
  cancel(): void {
    this.pendingTools = [];
    this.toolsThisResponse = 0;
    if (this.dc && this.dc.readyState === "open") {
      try {
        this.dc.send(JSON.stringify({ type: "response.cancel" }));
      } catch {
        /* ignore */
      }
    }
  }

  /** Send a text message into the voice session (e.g. typed input). */
  sendText(text: string): void {
    if (!this.dc || this.dc.readyState !== "open") return;
    this.dc.send(
      JSON.stringify({
        type: "conversation.item.create",
        item: { type: "message", role: "user", content: [{ type: "input_text", text }] },
      }),
    );
    this.dc.send(JSON.stringify({ type: "response.create" }));
  }

  private ensureAnalyser(): AnalyserNode {
    if (!this.audioCtx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.audioCtx = new Ctor();
    }
    void this.audioCtx.resume();
    if (!this.analyser) {
      this.analyser = this.audioCtx.createAnalyser();
      this.analyser.fftSize = 1024;
      this.analyser.smoothingTimeConstant = 0.8;
    }
    return this.analyser;
  }

  /** Live analyser node combining mic + assistant audio, for the waveform. */
  getAnalyser(): AnalyserNode | null {
    return this.analyser ?? null;
  }

  isConnected(): boolean {
    return !!this.dc && this.dc.readyState === "open";
  }

  hasMic(): boolean {
    return !!this.micTrack;
  }

  /** Enable/disable the mic ("listening") without renegotiating. */
  setListening(on: boolean): boolean {
    if (!this.micTrack) return false;
    this.micTrack.enabled = on;
    return true;
  }

  disconnect(): void {
    // Drop any in-flight model response/tool work so nothing tries to keep talking.
    this.cancel();
    this.micStream?.getTracks().forEach((t) => t.stop());
    this.dc?.close();
    this.pc?.close();
    // Stop assistant voice playback: pause, detach the stream (which also stops
    // its tracks), and tear down the element so no buffered audio keeps playing.
    if (this.audioEl) {
      try {
        this.audioEl.pause();
        const src = this.audioEl.srcObject as MediaStream | null;
        src?.getTracks().forEach((t) => t.stop());
        this.audioEl.srcObject = null;
        this.audioEl.removeAttribute("src");
        this.audioEl.load();
      } catch {
        /* best-effort teardown */
      }
    }
    void this.audioCtx?.close().catch(() => {});
    this.audioEl = undefined;
    this.audioCtx = undefined;
    this.analyser = undefined;
    this.micTrack = undefined;
    this.micStream = undefined;
    this.pc = undefined;
    this.dc = undefined;
    this.cb.onStatus?.("idle");
  }
}

function extractToken(token: { value?: string; client_secret?: { value: string } }): string | null {
  if (token?.value) return token.value;
  if (token?.client_secret?.value) return token.client_secret.value;
  return null;
}
